import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useCreateEventDeliverable } from "../../hooks/useEventDeliverables";
import {
  deliverableSchema,
  type DeliverableFormData,
} from "../../validators/deliverable.validators";
import { DeliverableFormFields } from "./DeliverableFormFields";

export interface AddDeliverableModalProps {
  eventId: string;
  children?: React.ReactNode;
}

export const AddDeliverableModal: React.FC<AddDeliverableModalProps> = ({
  eventId,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const createDeliverable = useCreateEventDeliverable();

  const form = useForm<DeliverableFormData>({
    resolver: zodResolver(deliverableSchema),
    defaultValues: {
      influencer_name: "",
      platform: "",
      status: "",
      expected_deliverable: "",
      content_url: "",
      notes: "",
    },
  });

  const onSubmit = async (data: DeliverableFormData) => {
    try {
      await createDeliverable.mutateAsync({
        event_id: eventId,
        influencer_name: data.influencer_name,
        platform: data.platform,
        status: data.status || "In Progress",
        expected_deliverable: data.expected_deliverable,
        deadline: data.deadline ? format(data.deadline, "yyyy-MM-dd") : null,
        content_url: data.content_url || null,
        notes: data.notes || null,
      });

      form.reset();
      setOpen(false);
    } catch (error) {
      console.error("Failed to create deliverable:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button className="bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add Deliverable
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Deliverable</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DeliverableFormFields form={form} />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={createDeliverable.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createDeliverable.isPending}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {createDeliverable.isPending ? "Adding..." : "Add Deliverable"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
